/**
 * feedback_controller.js — the bug report / feature request form in the Chip
 * widget's Support pane (Stimulus).
 *
 * Bound to templates/components/support_pane.html, which holds two views: the
 * chat view (home) and the form view (templates/components/_feedback_form.html,
 * the one copy of the form). open() preselects the type and shows the form;
 * submit() posts it and returns to the chat view with a thank-you line.
 *
 * The URL comes from the template: submitUrl is /feedback/submit. A ticket
 * raised from the support chat (the 'support-chat:ticket' event, see
 * openTicket) sets conversationTicketUrl and a source message, and the form
 * posts there instead, prefilled from the message. page_url is stamped at
 * submit time so it names the page the user was on, not the one the widget
 * loaded on.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ['form', 'pageUrl', 'typeSelect', 'title', 'description', 'anonymous',
                      'anonymousField', 'chatView', 'formView', 'sent'];
    static values = {
        submitUrl: String,
        conversationTicketUrl: { type: String, default: '' },
    };

    // data-action="click->feedback#open" with data-feedback-type-param.
    open({ params }) {
        this._setTicket('', null);
        if (params.type) this.typeSelectTarget.value = params.type;
        this.sentTarget.hidden = true;
        this._showForm(true);
    }

    // data-action="support-chat:ticket->feedback#openTicket": a ticket from the chat.
    openTicket({ detail }) {
        this.formTarget.reset();
        this._setTicket(detail.url, detail.sourceMessageId);
        this.typeSelectTarget.value = 'bug';
        this.titleTarget.value = detail.title;
        this.descriptionTarget.value = detail.description;
        this.sentTarget.hidden = true;
        this._showForm(true);
    }

    _setTicket(url, sourceMessageId) {
        this.conversationTicketUrlValue = url;
        this._sourceMessageId = sourceMessageId;
        this.anonymousFieldTarget.hidden = Boolean(url);
    }

    back() {
        this._showForm(false);
    }

    // data-action="submit->feedback#submit" on the form.
    async submit(event) {
        event.preventDefault();
        this.pageUrlTarget.value = window.location.href;
        if (!this.formTarget.reportValidity()) return;
        try {
            const result = await this._post(this._payload());
            if (!result.success) throw new Error(result.message || 'Unknown error');
            this.formTarget.reset();   // back to the template's defaults (priority medium)
            this._setTicket('', null);
            this._showForm(false);
            this.sentTarget.hidden = false;
        } catch (error) {
            console.error('Feedback submission error:', error);
            window.showErrorMessage('Error submitting feedback: ' + error.message);
        }
    }

    _payload() {
        const data = Object.fromEntries(new FormData(this.formTarget));
        if (this.conversationTicketUrlValue) {
            delete data.is_anonymous;
            data.source_message_id = this._sourceMessageId;
        } else {
            data.is_anonymous = this.anonymousTarget.checked;
        }
        return data;
    }

    async _post(data) {
        const response = await fetch(this.conversationTicketUrlValue || this.submitUrlValue, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': document.querySelector('meta[name=csrf-token]').getAttribute('content'),
            },
            credentials: 'same-origin',
            body: JSON.stringify(data),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || result.message || `HTTP ${response.status}`);
        return result;
    }

    _showForm(showing) {
        this.formViewTarget.hidden = !showing;
        this.chatViewTarget.hidden = showing;
    }
}
